import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError, timeout, tap } from 'rxjs/operators';

import { PaymentMethodCountry } from 'src/app/domain/PaymentMethodCountry';
import { Country } from 'src/app/domain/Country';

import { ValidationsCountry } from '../shared/interfaces/country-validation.interface';
import { IpService } from './Ip.service';
import { LocalStorageService } from './local-storage.service';

// Variable global para acceso rápido desde el router matcher
// Se actualiza cuando se cargan los países
let CACHED_COUNTRY_CODES: Set<string> = new Set([
  'ES','IT','PT','PL','RO','HR','GE','KZ','KG','MA',
  'CI','UA','RS','SI','BG','BA','ME','AD','AM','KE','GH','MD'
]);

export interface CountryConfigResponse {
  code: number;
  country: ApiCountryConfig;
}

export interface ApiCountryConfig {
  locale: string;
  minAddressChars: number;
  maxAddressChars: number;
  minCityChars: number;
  minPhoneChars: number;
  checkPostalCodeExists: boolean;
  twoLetterCodeProvinceOH: boolean;
  bankTransferLimitFloor: number;
  bankTransferActive: boolean;
  bankTransferAccount?: string;
  vatRegex?: string;
  postalCodeRegex?: string;
  phoneRegex?: string;
  maxOrderNotesChars?: number;
  paymentMethodCountries?: PaymentMethodCountry[];
  pickupPointsIntegration?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private readonly http = inject(HttpClient);
  private readonly ipService = inject(IpService);
  private readonly localStorageService = inject(LocalStorageService);
  private countries: Country[] = [];
  private readonly STORAGE_KEY = '[SHOP] countries';
  private readonly CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 horas

  public getCountryConfig(countryCode: string): Observable<ApiCountryConfig> {
    return this.http.get<CountryConfigResponse>(`country/config/${countryCode}`, { params: { context: 'shop' } }).pipe(
      map((response) => response.country)
    );
  }

  public getValidations(countryCode: string): Observable<ValidationsCountry> {
    return this.http
      .get<CountryConfigResponse>(`country/validations/${countryCode}`)
      .pipe(map((data) => data.country as ValidationsCountry));
  }

  public getGeolocation(): Observable<Country | null> {
    return this.ipService.getPublicIp().pipe(
      switchMap((ipData) => {
        if (!ipData?.ip) {
          console.error('getPublicIp no devolvió una IP válida', ipData);
          return of(null);
        }
        const requestBody = { ip: ipData.ip };
        return this.http.post<any>('country/geolocation', requestBody).pipe(
          catchError((err) => {
            console.error('Error en country/geolocation', err);
            return of(null);
          })
        );
      }),
      map((data) => {
        if (!data?.countryCode) {
          console.warn('Respuesta de geolocalización sin countryCode', data);
          return null;
        }
        const country = this.countries.find(
          (c) => c.code === data.countryCode
        );
        if (!country) {
          console.warn('Country no encontrado en availableCountries', data.countryCode);
          return null;
        }

        return country;
      }),
      timeout(3000),
      catchError((err) => {
        console.error('Error general en getGeolocation, usando fallback', err);
        return of(null);
      })
    );
  }

  public validateVat(countryCode: string, vat: string) {
    return this.http.post('country/validations/vat', { countryCode, vat }).pipe(
      map((data: any) => data.code === 200 && data.isValid === true)
    );
  }

  public getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>('country/list');
  }

  public loadCountries(): Observable<Country[]> {
    return this.getCountries().pipe(
      tap((countries: Country[]) => {
        this.setCountries(countries);
        this.localStorageService.save(this.STORAGE_KEY, countries);
      })
    );
  }

  public loadCountriesWithCache(): Country[] | null {
    const cachedCountries = this.localStorageService.get<Country[]>(this.STORAGE_KEY, this.CACHE_DURATION_MS);
    if (cachedCountries) {
      this.setCountries(cachedCountries);
      return cachedCountries;
    }
    return null;
  }

  public refreshCountriesInBackground(): void {
    this.getCountries().subscribe((countries: Country[]) => {
      this.setCountries(countries);
      this.localStorageService.save(this.STORAGE_KEY, countries);
    });
  }

  private setCountries(countries: Country[]): void {
    this.countries = countries;
    // Actualizar cache global de códigos para el router matcher
    CACHED_COUNTRY_CODES = new Set(countries.map(c => c.code).filter(Boolean) as string[]);
  }

  public get availableCountries(): Country[] {
    return [...this.countries]; // Retornar copia para evitar mutaciones
  }

  public getCountryByCode(code: string): Country | undefined {
    return this.countries.find(c => c.code === code);
  }

  public hasCountryCode(code: string): boolean {
    return this.countries.some(c => c.code === code);
  }

  // Método estático para acceso desde el router matcher
  public static hasCountryCodeCached(code: string): boolean {
    return CACHED_COUNTRY_CODES.has(code);
  }
}
